import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { Quote, ChevronRight } from 'lucide-react';

// Referans verileri - Kopyalar temizlendi
const clients = [
  { name: 'Şölen Cafe', id: 1, industry: 'Cafe/Restoran' },
  { name: 'Gorushbaby', id: 2, industry: 'Bebek Giyim Mağazaları' },
  { name: 'Forum Yaşam Cafe', id: 3, industry: 'Cafe/Restoran' },
  { name: 'Nous Cafe', id: 4, industry: 'Cafe/Restoran' },
  { name: 'AYS Event House', id: 5, industry: 'Eğlence Merkezi' },
  { name: 'MEC Bride', id: 6, industry: 'Moda' },
  { name: 'Rafine Brands', id: 7, industry: 'İçerik Stüdyosu' },
  { name: 'N-fit Health', id: 8, industry: 'Fitness' },
];

// Müşteri yorumları
const testimonials = [
  {
    id: 1,
    name: 'Doğukan Deniz',
    role: 'Rafine Brands',
    content: 'GEVEZ SOFTWARE ile çalışmak gerçek bir keyifti. Projemizi zamanında ve beklentilerin üzerinde teslim ettiler.',
  },
  {
    id: 2,
    name: 'İpek Yaşat',
    role: 'Forum Yaşam Cafe',
    content: 'Teknik uzmanlıkları ve profesyonel yaklaşımları ile en karmaşık sorunlarımıza bile çözüm buldular.',
  },
  {
    id: 3,
    name: 'Öznur Doğan Yılmaz',
    role: 'MEC Bride',
    content: 'Otomasyon sistemimizi sıfırdan kurarak iş süreçlerimizde %40 verimlilik artışı sağladılar.',
  },
];

// Logo Bileşeni (Resim yollarını ve hataları yönetir)
const ClientLogo = ({ name, id, isGrid = false }) => (
  <div className={`flex items-center gap-3 group cursor-pointer transition-all duration-300 hover:scale-105 ${isGrid ? 'flex-col' : ''}`}>
    <div className={`${isGrid ? 'w-16 h-16' : 'w-12 h-12'} flex items-center justify-center relative overflow-hidden rounded-lg bg-white/5 border border-white/10 group-hover:border-brand-cyan/30 transition-all shadow-2xl`}>
      <img 
        src={`/assets/logos/${name.toLowerCase().replace(/\s/g, '-')}.png`} 
        alt={name}
        className="w-full h-full object-cover p-0 opacity-50 grayscale group-hover:opacity-100 group-hover:grayscale-0 transition-all duration-500"
        onError={(e) => { 
          e.target.style.display = 'none'; 
          if(e.target.nextSibling) e.target.nextSibling.style.display = 'block'; 
        }} 
      />
      <span className="hidden font-display text-brand-cyan font-bold text-xs text-center p-1 uppercase">
        {name.split(' ').map(n => n[0]).join('')}
      </span>
    </div>
    {!isGrid && (
      <span className="font-mono text-sm text-white/40 group-hover:text-brand-cyan transition-colors duration-300 whitespace-nowrap">
        {name}
      </span>
    )}
  </div>
);

function ReferencesPage() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  return (
    <div className="pt-24" data-testid="references-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-background-primary relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 229, 255, 0.3) 1px, transparent 1px),
                              linear-gradient(90deg, rgba(0, 229, 255, 0.3) 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
          }}
        />

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-xs tracking-[0.3em] uppercase text-brand-cyan font-mono mb-4">
              Referanslarımız
            </p>
            <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Güvenilir <span className="gradient-text">İş Ortakları</span>
            </h1>
            <p className="font-mono text-text-secondary max-w-2xl mx-auto">
              Sektörlerinde önde gelen firmalar ile başarılı projeler gerçekleştirdik.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Animasyonlu Logo Şeridi (Marquee) */}
      <section ref={sectionRef} className="py-16 bg-background-secondary overflow-hidden w-full relative">
        <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-background-secondary to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-background-secondary to-transparent z-10 pointer-events-none" />

        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8 }}
          className="flex flex-col gap-8"
        >
          {/* Üst Sıra - Sola Kayar */}
          <Marquee speed={50} direction="left" gradient={false} pauseOnHover={true}>
            {clients.map((client) => (
              <div key={`marquee-top-${client.id}`} className="mx-12 py-4">
                <ClientLogo name={client.name} id={client.id} />
              </div>
            ))}
          </Marquee>

          {/* Alt Sıra - Sağa Kayar */}
          <Marquee speed={50} direction="right" gradient={false} pauseOnHover={true}>
            {[...clients].reverse().map((client) => (
              <div key={`marquee-bottom-${client.id}`} className="mx-12 py-4">
                <ClientLogo name={client.name} id={client.id} />
              </div>
            ))}
          </Marquee>
        </motion.div>
      </section>

      {/* Sektörler Grid Alanı - Logolar Tam Dolu */}
      <section className="py-20 bg-background-primary">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-4">
              Çalıştığımız <span className="gradient-text">Sektörler</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {clients.map((client, index) => (
              <motion.div
                key={`grid-${client.id}-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass p-8 text-center group hover:border-brand-cyan/30 transition-all duration-300 flex flex-col items-center"
              >
                {/* Grid içindeki logo kutusu */}
                <div className="w-20 h-20 mb-4 flex items-center justify-center rounded-lg bg-white/5 group-hover:bg-brand-cyan/10 border border-white/5 group-hover:border-brand-cyan/30 overflow-hidden transition-all duration-300">
                  <img 
                    src={`/assets/logos/${client.name.toLowerCase().replace(/\s/g, '-')}.png`} 
                    alt={client.name}
                    className="w-full h-full object-cover p-0 opacity-60 group-hover:opacity-100 transition-all duration-500"
                    onError={(e) => { e.target.src = "https://ui-avatars.com/api/?name=" + client.name + "&background=0D0E19&color=00E5FF"; }}
                  />
                </div>
                <h3 className="font-display text-white font-bold mb-1">{client.name}</h3>
                <p className="font-mono text-xs text-text-muted">{client.industry}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-background-secondary">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <p className="text-xs tracking-[0.3em] uppercase text-brand-cyan font-mono mb-4">
              Müşteri Yorumları
            </p>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-white">
              Ne <span className="gradient-text">Diyorlar?</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={`testi-${testimonial.id}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass p-8 rounded-lg relative text-left border border-white/5"
              >
                <Quote className="absolute top-6 right-6 w-8 h-8 text-brand-cyan/20" />
                <p className="font-mono text-text-secondary text-sm leading-relaxed mb-6 italic">
                  "{testimonial.content}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-brand-cyan/20 flex items-center justify-center border border-brand-cyan/30">
                    <span className="font-display text-brand-cyan font-bold">
                      {testimonial.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <h4 className="font-display text-white font-bold text-sm">{testimonial.name}</h4>
                    <p className="font-mono text-xs text-text-muted">{testimonial.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-background-primary">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-display text-2xl md:text-3xl font-bold text-white mb-6"
          >
            Siz de <span className="gradient-text">Referansımız</span> Olun
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <Link
              to="/iletisim"
              className="inline-flex items-center gap-2 px-8 py-4 bg-brand-cyan text-background-primary font-display font-bold text-sm uppercase tracking-wider
                         hover:bg-white hover:shadow-[0_0_40px_rgba(0,229,255,0.6)] transition-all duration-300 rounded-sm"
            >
              Proje Başlat <ChevronRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default ReferencesPage;